import React, { useEffect, useState } from "react";

export default function DetailReservationViaEmail({ reference }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(
      `https://djatervoyage.com/backend_djatervoyage/reservation/details.php?ref=${reference}`,
      { method: "GET", headers: { "Content-Type": "application/json" } }
    )
      .then((res) => res.json())
      .then((result) => {
        if (result.error) {
          setError(result.error);
        } else {
          setData(result);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Erreur de connexion au serveur");
        setLoading(false);
      });
  }, [reference]);

  // Helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr; // au cas où la date est déjà lisible
    return d.toLocaleString("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return "N/A";
    return duration
      .replace("PT", "")
      .replace("H", "h ")
      .replace("M", "min");
  };

  const handlePrint = () => {
    const printContents = document.getElementById("print-zone").innerHTML;
    const win = window.open("", "", "width=800,height=600");
    win.document.write(printContents);
    win.document.close();
    win.print();
  };

  if (loading)
    return (
      <div className="flex justify-center items-center p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-blue-700">Chargement...</span>
      </div>
    );

  if (error)
    return (
      <div className="p-4 bg-red-100 text-red-700 border border-red-300 rounded-lg">
        ❌ {error}
      </div>
    );

  if (!data) return null;

  return (
    <div className="max-w-4xl mx-auto">
      <div
        id="print-zone"
        className="bg-gradient-to-br from-blue-50 to-white shadow-2xl rounded-3xl p-8 border border-blue-100"
      >
        <h1 className="text-3xl font-extrabold text-blue-700 text-center mb-6 tracking-tight">
          ✈️ Confirmation de Réservation
        </h1>

        {/* Infos réservation */}
        <section className="mb-8">
          <h2 className="text-xl font-bold border-b pb-2 mb-2 text-blue-600">
            Informations Réservation
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p>
                <strong>Référence :</strong>{" "}
                <span className="text-blue-800">{data.reference || "N/A"}</span>
              </p>
              <p>
                <strong>Date de création :</strong> {formatDate(data.created_at)}
              </p>
              <p>
                <strong>Statut :</strong>{" "}
                <span
                  className={`font-bold ${
                    data.reservation_status === "confirmée"
                      ? "text-green-600"
                      : "text-orange-600"
                  }`}
                >
                  {data.reservation_status || "N/A"}
                </span>
              </p>
              <p>
                <strong>Paiement :</strong>{" "}
                <span
                  className={`font-bold ${
                    data.payment_status === "payé"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {data.payment_status || "N/A"}
                </span>
              </p>
            </div>

          </div>
        </section>

        {/* Passagers */}
        <section className="mb-8">
          <h2 className="text-xl font-bold border-b pb-2 mb-2 text-blue-600">
            Passagers
          </h2>
          <table className="w-full border-collapse rounded-lg overflow-hidden shadow">
            <thead>
              <tr className="bg-blue-100">
                <th className="p-2">Nom</th>
                <th>Date de Naissance</th>
                <th>Genre</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Nationalité</th>
                <th>Document</th>
              </tr>
            </thead>
            <tbody>
              {data.passenger_data?.map((p, i) => (
                <tr key={`passenger-${i}`} className="border-b hover:bg-blue-50">
                  <td className="p-2 font-semibold">
                    {p.name?.firstName || ""} {p.name?.lastName || ""}
                  </td>
                  <td>{formatDate(p.dateOfBirth)}</td>
                  <td>{p.gender || "N/A"}</td>
                  <td>{p.contact?.emailAddress || "N/A"}</td>
                  <td>
                    {p.contact?.phones?.[0]?.countryCallingCode
                      ? "+" + p.contact.phones[0].countryCallingCode
                      : ""}
                    {p.contact?.phones?.[0]?.number || ""}
                  </td>
                  <td>{p.nationality || "N/A"}</td>
                  <td>
                    {p.document?.type
                      ? `${p.document.type}: ${p.document.number}`
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Vols */}
        <section className="mb-8">
          <h2 className="text-xl font-bold border-b pb-2 mb-2 text-blue-600">
            Détails du Vol
          </h2>
          <table className="w-full border-collapse rounded-lg overflow-hidden shadow">
            <thead>
              <tr className="bg-blue-100">
                <th className="p-2">Départ</th>
                <th>Arrivée</th>
                <th>Compagnie</th>
                <th>Vol</th>
                <th>Durée</th>
                <th>Classe</th>
                <th>CO₂</th>
              </tr>
            </thead>
            <tbody>
              {data.flight_data?.map((f, i) =>
                f.itineraries?.map((it, j) =>
                  it.segments?.map((seg, k) => (
                    <tr
                      key={`flight-${i}-${j}-${k}`}
                      className="border-b hover:bg-blue-50"
                    >
                      <td className="p-2">
                        <span className="font-semibold">
                          {seg.departure?.iataCode || "N/A"}
                        </span>
                        <br />
                        <span className="text-xs text-gray-500">
                          {formatDate(seg.departure?.at)}
                        </span>
                      </td>
                      <td>
                        <span className="font-semibold">
                          {seg.arrival?.iataCode || "N/A"}
                        </span>
                        <br />
                        <span className="text-xs text-gray-500">
                          {formatDate(seg.arrival?.at)}
                        </span>
                      </td>
                      <td>{seg.carrierCode || "N/A"}</td>
                      <td>{seg.number || "N/A"}</td>
                      <td>{formatDuration(seg.duration)}</td>
                      <td>{seg.co2Emissions?.[0]?.cabin || "N/A"}</td>
                      <td>
                        {seg.co2Emissions?.[0]?.weight
                          ? `${seg.co2Emissions[0].weight} kg`
                          : "N/A"}
                      </td>
                    </tr>
                  ))
                )
              )}
            </tbody>
          </table>
        </section>

        {/* Paiement */}
        <section className="mb-8">
          <h2 className="text-xl font-bold border-b pb-2 mb-2 text-blue-600">
            Informations de Paiement
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p>
                <strong>Méthode :</strong> {data.payment_method || "N/A"}
              </p>
              <p>
                <strong>Date :</strong> {formatDate(data.payment_date)}
              </p>
            </div>
            <div>
              
              <p>
                <strong>Référence de paiement :</strong>{" "}
                {data.payment_reference || "N/A"}
              </p>
            </div>
          </div>
        </section>

        {/* Conditions */}
        <section className="mb-8">
          <h2 className="text-xl font-bold border-b pb-2 mb-2 text-blue-600">
            Conditions & Infos Utiles
          </h2>
          <ul className="list-disc ml-6 text-gray-700">
            <li>Veuillez vérifier les informations de votre vol avant le départ.</li>
            <li>Présentez-vous à l'aéroport au moins 2 heures avant le départ.</li>
            <li>
              Pour toute modification ou annulation, contactez notre service
              client.
            </li>
            <li>Les billets sont soumis aux conditions de la compagnie aérienne.</li>
          </ul>
        </section>
      </div>

      <button
        onClick={handlePrint}
        aria-label="Imprimer la réservation"
        className="mt-6 px-6 py-3 bg-blue-700 text-white rounded-xl shadow-lg hover:bg-blue-800 transition"
      >
        🖨️ Imprimer la Réservation
      </button>
    </div>
  );
}
